import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { initialState } from './state';
import * as api from 'src/api/product';
import { ProductTagGroup } from 'src/admin-entity/interface/product/product-tag-group.interface';
import client from 'src/api/client';
import { BASE_URL } from 'src/config/api';
import { ProductOld } from 'src/admin-entity/interface/product/product-old.interface';
import { AppState } from 'src/store';
import { getErrorMessage } from 'src/utils/utilService';

export const getProductTagGroups = createAsyncThunk(
    'productTagSlice/getProductTagGroups',
    async (_, { rejectWithValue }) => {
        try {
            const res = await api.getProductTagGroups();
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const getChildTags = createAsyncThunk(
    `productTagSlice/getChildTags`,
    async (tagId: number, { rejectWithValue }) => {
        try {
            const res = await api.getChildrenTagList(tagId);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const addTagGroup = createAsyncThunk(
    `productTagSlice/addTagGroup`,
    async (title: string, { rejectWithValue }) => {
        try {
            const res = await api.addTagGroup(title);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const addChildTag = createAsyncThunk(
    `productTagSlice/addChildTag`,
    async ({ parentId, tagName }: { parentId: number; tagName: string }, { rejectWithValue }) => {
        try {
            const res = await api.addChildTag(parentId, tagName);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const getProductsInTag = createAsyncThunk(
    `productTagSlice/getProductsInTag`,
    async (_, { rejectWithValue, getState }) => {
        try {
            const page = (getState() as AppState).productTag.page;
            const take = (getState() as AppState).productTag.take;
            const selectedGroupId = (getState() as AppState).productTag.productsInTag.selectedGroupId;
            const res = await client.get<{ page: { count: number }; products: ProductOld[] }>(
                `${BASE_URL}/v1/admin/products/search?productTagGroupIds=${selectedGroupId}${
                    page ? `&page=${page}` : ''
                }${take ? `&take=${take}` : ''}`,
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const addProductsToTag = createAsyncThunk(
    `productTagSlice/addProductsToTag`,
    async (productIds: number[], { rejectWithValue, getState }) => {
        try {
            const productTag = (getState() as AppState).productTag;
            const tagId = productTag.selectedChildTag.id
                ? productTag.selectedChildTag.id
                : productTag.selectedParentTagGroup.id;
            await api.addProductsToTag(tagId, productIds);
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const deleteProductsFromTag = createAsyncThunk(
    `productTagSlice/deleteProductsFromTag`,
    async (productIds: number[], { rejectWithValue, getState }) => {
        try {
            const productTag = (getState() as AppState).productTag;
            const tagId = productTag.selectedChildTag.id
                ? productTag.selectedChildTag.id
                : productTag.selectedParentTagGroup.id;
            await api.deleteProductsFromTag(tagId, productIds);
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const deleteParentTag = createAsyncThunk(
    `productTagSlice/deleteParentTag`,
    async (tagId: number, { rejectWithValue }) => {
        try {
            await api.deleteTag(tagId);
            return tagId;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const deleteChildTag = createAsyncThunk(
    `productTagSlice/deleteChildTag`,
    async (tagId: number, { rejectWithValue }) => {
        try {
            await api.deleteTag(tagId);
            return tagId;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const updateParentTag = createAsyncThunk(
    `productTagSlice/updateParentTag`,
    async ({ id, title }: { id: number; title: string }, { rejectWithValue }) => {
        try {
            await api.updateParentTag(id, title);
        } catch (err) {
            return rejectWithValue(getErrorMessage(err));
        }
    },
);

export const updateChildTag = createAsyncThunk(
    `productTagSlice/updateChildTag`,
    async ({ id, tag }: { id: number; tag: string }, { rejectWithValue }) => {
        try {
            await api.updateChildTag(id, tag);
        } catch (err) {
            return rejectWithValue(getErrorMessage(err));
        }
    },
);

export const getProductTagGroupDetail = createAsyncThunk(
    `productTagSlice/getProductTagGroupDetail`,
    async (tagGroupId: number, { rejectWithValue }) => {
        try {
            console.log(tagGroupId);
            const res = await api.getProductTagGroupDetail(tagGroupId);
            return res.data;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err));
        }
    },
);

export const productTagSlice = createSlice({
    name: 'productTagSlice',
    initialState,
    reducers: {
        init: state => {
            state.tagGroups = [];
            state.childrenTags = [];
            state.selectedParentTagGroup = {
                id: 0,
                title: '',
                children: [],
            };
            state.productsInTag = {
                selectedGroupId: 0,
                page: {
                    count: 0,
                },
                products: [],
            };
            state.page = 0;
            state.take = 10;
            state.selectedChildTag = {
                id: 0,
                tag: '',
            };
        },
        selectTagGroup: (state, action: PayloadAction<ProductTagGroup>) => {
            state.selectedParentTagGroup = action.payload;
            state.productsInTag.selectedGroupId = action.payload.id;
        },
        selectChildTag: (state, action: PayloadAction<{ id: number; tag: string }>) => {
            state.selectedChildTag = action.payload;
            state.productsInTag.selectedGroupId = action.payload.id;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        setTake: (state, action: PayloadAction<number>) => {
            state.take = action.payload;
        },
        setChildrenTags: (state, action: PayloadAction<{ id: number; tag: string }[]>) => {
            state.childrenTags = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(getProductTagGroups.fulfilled, (state, action) => {
            state.tagGroups = action.payload;
        });
        builder.addCase(addChildTag.fulfilled, (state, action) => {
            state.childrenTags.push({ id: action.payload.id, tag: action.payload.tag });
        });
        builder.addCase(addTagGroup.fulfilled, (state, action) => {
            state.tagGroups.push(action.payload);
        });
        builder.addCase(getProductsInTag.fulfilled, (state, action) => {
            state.productsInTag.page = action.payload.page;
            state.productsInTag.products = action.payload.products;
        });
        builder.addCase(deleteParentTag.fulfilled, (state, action) => {
            state.tagGroups = state.tagGroups.filter(tag => tag.id !== action.payload);
        });
        builder.addCase(deleteChildTag.fulfilled, (state, action) => {
            state.childrenTags = state.childrenTags.filter(tag => tag.id !== action.payload);
        });
        builder.addCase(getProductTagGroupDetail.fulfilled, (state, action) => {
            state.childrenTags = action.payload.children;
        });
    },
});

export const { init, selectTagGroup, selectChildTag, setPage, setTake, setChildrenTags } = productTagSlice.actions;
export default productTagSlice.reducer;
